import { useState, useMemo } from 'react';
import { WeeklySchedule, DayOfWeek, validateWeeklySchedule } from '@/models/individual-schedule.model';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Save, RotateCcw, AlertCircle, Clock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';

const DAYS: { key: DayOfWeek; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

type ScheduleEditorProps = {
  weeklySchedule: WeeklySchedule;
  saving: boolean;
  saveSchedule: (schedule: WeeklySchedule) => Promise<void>;
};

export function ScheduleEditor({ weeklySchedule, saving, saveSchedule }: ScheduleEditorProps) {
  const [currentSchedule, setCurrentSchedule] = useState<WeeklySchedule>(weeklySchedule);
  const [hasChanges, setHasChanges] = useState(false);

  const isValid = useMemo(() => validateWeeklySchedule(currentSchedule), [currentSchedule]);
  const validationError = isValid ? null : 'Please ensure all enabled days have valid start and end times, and start time is before end time.';

  const handleDayToggle = (day: DayOfWeek, enabled: boolean) => {
    setCurrentSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        enabled,
        start_time: enabled && !prev[day].start_time ? '09:00' : prev[day].start_time,
        end_time: enabled && !prev[day].end_time ? '17:00' : prev[day].end_time,
      }
    }));
    setHasChanges(true);
  };

  const handleTimeChange = (day: DayOfWeek, field: 'start_time' | 'end_time', value: string) => {
    setCurrentSchedule(prev => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.error("Invalid Schedule", {
        description: "Please fix the validation errors before saving.",
      });
      return;
    }

    try {
      await saveSchedule(currentSchedule);
      setHasChanges(false);
      toast.success("Schedule Saved", {
        description: "Your work schedule has been updated successfully.",
      });
    } catch (error) {
      toast.error("Save Failed", {
        description: error instanceof Error ? error.message : "Failed to save schedule",
      });
    }
  };

  const handleReset = () => {
    setCurrentSchedule(weeklySchedule);
    setHasChanges(false);
  };

  const applyToAllWorkingDays = () => {
    const firstEnabledDay = DAYS.find(day => currentSchedule[day.key].enabled);
    if (!firstEnabledDay) return;

    const template = currentSchedule[firstEnabledDay.key];
    const workingDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] as DayOfWeek[];
    
    setCurrentSchedule(prev => {
      const updated = { ...prev };
      workingDays.forEach(day => {
        if (updated[day].enabled) {
          updated[day] = {
            ...updated[day],
            start_time: template.start_time,
            end_time: template.end_time,
          };
        }
      });
      return updated;
    });
    setHasChanges(true);
    
    toast.success("Times Applied", {
      description: "Working hours have been applied to all enabled weekdays.",
    });
  };

  return (
    <div className="space-y-6">
      {validationError && (
        <div className="flex items-center gap-2 p-4 border border-destructive/50 rounded-lg bg-destructive/10">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{validationError}</p>
        </div>
      )}

      <div className="space-y-4">
        {DAYS.map(({ key: day, label }) => {
          const daySchedule = currentSchedule[day];
          return (
            <Card key={day} className={`transition-colors py-2 ${daySchedule.enabled ? 'border-primary/50 bg-primary/5' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Switch
                      id={`${day}-enabled`}
                      checked={daySchedule.enabled}
                      onCheckedChange={(enabled) => handleDayToggle(day, enabled)}
                    />
                    <Label htmlFor={`${day}-enabled`} className="text-base font-medium">
                      {label}
                    </Label>
                  </div>
                  
                  {daySchedule.enabled && (
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div className="flex items-center space-x-2">
                        <Input
                          type="time"
                          value={daySchedule.start_time || ''}
                          onChange={(e) => handleTimeChange(day, 'start_time', e.target.value)}
                          className="w-32"
                          aria-label={`${label} start time`}
                        />
                        <span className="text-muted-foreground">to</span>
                        <Input
                          type="time"
                          value={daySchedule.end_time || ''}
                          onChange={(e) => handleTimeChange(day, 'end_time', e.target.value)}
                          className="w-32"
                          aria-label={`${label} end time`}
                        />
                      </div>
                    </div>
                  )}
                  
                  {!daySchedule.enabled && (
                    <span className="text-sm text-muted-foreground">Not working</span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex items-center justify-between pt-4 border-t">
        <Button
          variant="outline"
          onClick={applyToAllWorkingDays}
          disabled={!DAYS.some(day => currentSchedule[day.key].enabled)}
        >
          Apply to All Weekdays
        </Button>

        <div className="flex items-center space-x-2">
          {hasChanges && (
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges || !isValid}
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Schedule'}
          </Button>
        </div>
      </div>
    </div>
  );
}